/**
 * Emissions Trend Chart Component
 * Displays emissions data with By month, By source, Scope 1, and Scope 2 tabs
 */

import React, { useState, useMemo } from 'react';
import {
  Box,
  Card,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Grid,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import { Business } from '@mui/icons-material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';

interface EmissionDataPoint {
  month?: number;
  year?: number;
  category?: string;
  subcategory?: string;
  fuelType?: string;
  source?: string;
  value: number;
  facility_id?: string;
  facility_name?: string;
}

interface Facility {
  id: string;
  name: string;
}

interface EmissionsTrendChartProps {
  data: EmissionDataPoint[];
  title?: string;
  color?: string;
  facilities?: Facility[];
  showScopeTabs?: boolean; // Whether to show Scope 1/2 tabs (default: true)
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Colors for scope-specific charts
const SCOPE1_COLORS = ['#f59e0b', '#fbbf24', '#fcd34d', '#fde68a', '#fef3c7'];
const SCOPE2_COLORS = ['#3b82f6', '#60a5fa', '#93c5fd', '#bfdbfe', '#dbeafe'];

export const EmissionsTrendChart: React.FC<EmissionsTrendChartProps> = ({
  data,
  title = 'Emissions',
  color = '#3b82f6',
  facilities = [],
  showScopeTabs = true,
}) => {
  const [viewMode, setViewMode] = useState<'month' | 'source' | 'scope1' | 'scope2'>('month');
  const [selectedFacility, setSelectedFacility] = useState<string>('all');

  // Handle facility filter change
  const handleFacilityChange = (event: SelectChangeEvent<string>) => {
    setSelectedFacility(event.target.value);
  };

  // Filter data by selected facility
  const filteredData = useMemo(() => {
    if (selectedFacility === 'all') {
      return data;
    }
    return data.filter(d => d.facility_id === selectedFacility);
  }, [data, selectedFacility]);

  // Filter data by scope (using filtered data)
  const scope1Data = useMemo(() => filteredData.filter(d => d.source === 'Scope 1'), [filteredData]);
  const scope2Data = useMemo(() => filteredData.filter(d => d.source === 'Scope 2'), [filteredData]);

  // Process data based on view mode
  const chartData = useMemo(() => {
    if (viewMode === 'month') {
      // Group by month
      const monthlyData: Record<string, number> = {};
      MONTHS.forEach((m) => {
        monthlyData[m] = 0;
      });

      filteredData.forEach(d => {
        if (d.month !== undefined && d.month >= 1 && d.month <= 12) {
          const monthName = MONTHS[d.month - 1];
          monthlyData[monthName] = (monthlyData[monthName] || 0) + d.value;
        }
      });

      return MONTHS.map(month => ({
        name: month,
        value: monthlyData[month] || 0,
      }));
    } else if (viewMode === 'source') {
      // Group by subcategory first, then category, then source
      const sourceData: Record<string, number> = {};
      filteredData.forEach(d => {
        // Prefer subcategory for more detail, fallback to category, then source
        const key = d.subcategory || d.category || d.source || 'Other';
        sourceData[key] = (sourceData[key] || 0) + d.value;
      });

      return Object.entries(sourceData)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 12) // Show top 12 categories
        .map(([name, value]) => ({
          name: name.length > 20 ? name.substring(0, 18) + '...' : name,
          fullName: name,
          value,
        }));
    } else {
      // For scope1 and scope2, return empty - handled separately
      return [];
    }
  }, [filteredData, viewMode]);

  // Scope 1 breakdown by fuel type (e.g., Diesel, LPG, Natural Gas)
  const scope1ChartData = useMemo(() => {
    const fuelTypeData: Record<string, number> = {};
    scope1Data.forEach(d => {
      // Use fuelType first, fallback to subcategory, then category
      const key = d.fuelType || d.subcategory || d.category || 'Other';
      fuelTypeData[key] = (fuelTypeData[key] || 0) + d.value;
    });

    return Object.entries(fuelTypeData)
      .sort(([, a], [, b]) => b - a)
      .map(([name, value]) => ({
        name: name.length > 25 ? name.substring(0, 22) + '...' : name,
        fullName: name,
        value,
      }));
  }, [scope1Data]);

  // Scope 1 monthly trend
  const scope1MonthlyData = useMemo(() => {
    const monthlyData: Record<string, number> = {};
    MONTHS.forEach((m) => {
      monthlyData[m] = 0;
    });

    scope1Data.forEach(d => {
      if (d.month !== undefined && d.month >= 1 && d.month <= 12) {
        const monthName = MONTHS[d.month - 1];
        monthlyData[monthName] = (monthlyData[monthName] || 0) + d.value;
      }
    });

    return MONTHS.map(month => ({
      name: month,
      value: monthlyData[month] || 0,
    }));
  }, [scope1Data]);

  // Scope 2 breakdown by category
  const scope2ChartData = useMemo(() => {
    const categoryData: Record<string, number> = {};
    scope2Data.forEach(d => {
      const key = d.category || 'Other';
      categoryData[key] = (categoryData[key] || 0) + d.value;
    });

    return Object.entries(categoryData)
      .sort(([, a], [, b]) => b - a)
      .map(([name, value]) => ({
        name: name.length > 25 ? name.substring(0, 22) + '...' : name,
        fullName: name,
        value,
      }));
  }, [scope2Data]);

  // Scope 2 monthly trend
  const scope2MonthlyData = useMemo(() => {
    const monthlyData: Record<string, number> = {};
    MONTHS.forEach((m) => {
      monthlyData[m] = 0;
    });

    scope2Data.forEach(d => {
      if (d.month !== undefined && d.month >= 1 && d.month <= 12) {
        const monthName = MONTHS[d.month - 1];
        monthlyData[monthName] = (monthlyData[monthName] || 0) + d.value;
      }
    });

    return MONTHS.map(month => ({
      name: month,
      value: monthlyData[month] || 0,
    }));
  }, [scope2Data]);

  // Calculate totals
  const scope1Total = scope1Data.reduce((sum, d) => sum + d.value, 0);
  const scope2Total = scope2Data.reduce((sum, d) => sum + d.value, 0);

  const formatValue = (value: number) => {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `${(value / 1000).toFixed(0)}K`;
    }
    return value.toFixed(0);
  };

  const formatTooltipValue = (value: number) => {
    return `${value.toLocaleString(undefined, { maximumFractionDigits: 2 })} tCO2e`;
  };

  // Render scope-specific chart (pie + line combo)
  const renderScopeChart = (
    scopeData: { name: string; fullName: string; value: number }[],
    monthlyData: { name: string; value: number }[],
    colors: string[],
    scopeTitle: string,
    scopeColor: string,
    total: number,
    breakdownLabel: string = 'By Category'
  ) => (
    <Grid container spacing={3}>
      {/* Pie Chart - Category/Fuel Type Breakdown */}
      <Grid item xs={12} md={5}>
        <Box sx={{ textAlign: 'center', mb: 1 }}>
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>
            {breakdownLabel}
          </Typography>
        </Box>
        <Box sx={{ height: 240 }}>
          {scopeData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={scopeData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={2}
                  dataKey="value"
                  nameKey="name"
                >
                  {scopeData.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value) => [formatTooltipValue(value as number), '']}
                  labelFormatter={(_, payload) => {
                    if (payload && payload[0] && payload[0].payload.fullName) {
                      return payload[0].payload.fullName;
                    }
                    return '';
                  }}
                  contentStyle={{ borderRadius: 8, border: '1px solid #e5e7eb', fontSize: '0.85rem' }}
                />
                <Legend
                  layout="vertical"
                  align="right"
                  verticalAlign="middle"
                  wrapperStyle={{ fontSize: '0.75rem', paddingLeft: 10 }}
                  formatter={(value) => <span style={{ color: '#374151' }}>{value}</span>}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <Typography sx={{ color: '#9ca3af', fontSize: '0.875rem' }}>No data available</Typography>
            </Box>
          )}
        </Box>
        <Box sx={{ textAlign: 'center', mt: 1 }}>
          <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: scopeColor }}>
            {total.toLocaleString(undefined, { maximumFractionDigits: 2 })}
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', color: '#6b7280' }}>tCO2e Total</Typography>
        </Box>
      </Grid>

      {/* Line Chart - Monthly Trend */}
      <Grid item xs={12} md={7}>
        <Box sx={{ textAlign: 'center', mb: 1 }}>
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>
            Monthly Trend
          </Typography>
        </Box>
        <Box sx={{ height: 260 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={monthlyData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 11, fill: '#6b7280' }}
                axisLine={{ stroke: '#e5e7eb' }}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 11, fill: '#6b7280' }}
                tickFormatter={formatValue}
                axisLine={{ stroke: '#e5e7eb' }}
                tickLine={false}
                width={50}
              />
              <Tooltip
                formatter={(value) => [formatTooltipValue(value as number), scopeTitle]}
                labelStyle={{ color: '#111827', fontWeight: 600 }}
                contentStyle={{ borderRadius: 8, border: '1px solid #e5e7eb', fontSize: '0.85rem' }}
              />
              <Line
                type="monotone"
                dataKey="value"
                stroke={scopeColor}
                strokeWidth={2}
                dot={{ fill: scopeColor, strokeWidth: 0, r: 4 }}
                activeDot={{ r: 6, fill: scopeColor }}
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      </Grid>
    </Grid>
  );

  return (
    <Card sx={{ border: '1px solid #e5e7eb', boxShadow: 'none', borderRadius: 3, mb: 3 }}>
      <Box sx={{ p: 2.5 }}>
        {/* Header with toggles */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(_, newMode) => newMode && setViewMode(newMode)}
              size="small"
              sx={{
                '& .MuiToggleButton-root': {
                  textTransform: 'none',
                  fontSize: '0.8rem',
                  fontWeight: 500,
                  px: 2,
                  py: 0.5,
                  color: '#6b7280',
                  borderColor: '#e5e7eb',
                  '&.Mui-selected': {
                    backgroundColor: '#f3f4f6',
                    color: '#111827',
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: '#e5e7eb',
                    },
                  },
                },
              }}
            >
              <ToggleButton value="month">By month</ToggleButton>
              <ToggleButton value="source">By source</ToggleButton>
              {showScopeTabs && (
                <ToggleButton value="scope1">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#f59e0b' }} />
                    Scope 1
                  </Box>
                </ToggleButton>
              )}
              {showScopeTabs && (
                <ToggleButton value="scope2">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#3b82f6' }} />
                    Scope 2
                  </Box>
                </ToggleButton>
              )}
            </ToggleButtonGroup>
          </Box>

          {/* Facility Filter Dropdown */}
          {facilities.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Business sx={{ fontSize: 18, color: '#6b7280' }} />
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <Select
                  value={selectedFacility}
                  onChange={handleFacilityChange}
                  displayEmpty
                  sx={{
                    fontSize: '0.8rem',
                    color: '#374151',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#e5e7eb',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#d1d5db',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#3b82f6',
                    },
                    '& .MuiSelect-select': {
                      py: 0.75,
                    },
                  }}
                >
                  <MenuItem value="all" sx={{ fontSize: '0.8rem' }}>
                    All Facilities
                  </MenuItem>
                  {facilities.map((facility) => (
                    <MenuItem key={facility.id} value={facility.id} sx={{ fontSize: '0.8rem' }}>
                      {facility.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
        </Box>

        {/* Chart Content */}
        {viewMode === 'scope1' ? (
          renderScopeChart(scope1ChartData, scope1MonthlyData, SCOPE1_COLORS, 'Scope 1', '#f59e0b', scope1Total, 'By Fuel Type')
        ) : viewMode === 'scope2' ? (
          renderScopeChart(scope2ChartData, scope2MonthlyData, SCOPE2_COLORS, 'Scope 2', '#3b82f6', scope2Total, 'By Energy Source')
        ) : (
          <Box sx={{ height: 280 }}>
            <ResponsiveContainer width="100%" height="100%">
              {viewMode === 'source' ? (
                <BarChart
                  data={chartData}
                  margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                  layout="vertical"
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" horizontal={true} vertical={false} />
                  <XAxis
                    type="number"
                    tick={{ fontSize: 11, fill: '#6b7280' }}
                    tickFormatter={formatValue}
                    axisLine={{ stroke: '#e5e7eb' }}
                    tickLine={false}
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    tick={{ fontSize: 11, fill: '#6b7280' }}
                    axisLine={{ stroke: '#e5e7eb' }}
                    tickLine={false}
                    width={120}
                  />
                  <Tooltip
                    formatter={(value) => [formatTooltipValue(value as number), title]}
                    labelFormatter={(label, payload) => {
                      if (payload && payload[0] && payload[0].payload.fullName) {
                        return payload[0].payload.fullName;
                      }
                      return label;
                    }}
                    labelStyle={{ color: '#111827', fontWeight: 600 }}
                    contentStyle={{ borderRadius: 8, border: '1px solid #e5e7eb', fontSize: '0.85rem' }}
                  />
                  <Bar
                    dataKey="value"
                    fill={color}
                    radius={[0, 4, 4, 0]}
                    maxBarSize={24}
                  />
                </BarChart>
              ) : (
                <LineChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 11, fill: '#6b7280' }}
                    axisLine={{ stroke: '#e5e7eb' }}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: '#6b7280' }}
                    tickFormatter={formatValue}
                    axisLine={{ stroke: '#e5e7eb' }}
                    tickLine={false}
                    width={50}
                  />
                  <Tooltip
                    formatter={(value) => [formatTooltipValue(value as number), title]}
                    labelStyle={{ color: '#111827', fontWeight: 600 }}
                    contentStyle={{ borderRadius: 8, border: '1px solid #e5e7eb', fontSize: '0.85rem' }}
                  />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke={color}
                    strokeWidth={2}
                    dot={{ fill: color, strokeWidth: 0, r: 4 }}
                    activeDot={{ r: 6, fill: color }}
                  />
                </LineChart>
              )}
            </ResponsiveContainer>
          </Box>
        )}
      </Box>
    </Card>
  );
};
